import httpService from "../../services/httpService";
import { ApiEndpoints } from "../../constants/apiEndpoints";
import { PaginationMeta } from "../../types/api";

export enum SongRequestStatus {
  PENDING = "pending",
  IN_PROGRESS = "in_progress",
  COMPLETED = "completed",
  REJECTED = "rejected",
}

export type SongRequestPriority = "low" | "normal" | "high" | "urgent";
export type SongRequestPushStatus = "pending" | "sent" | "failed" | "skipped";

export type SongRequestSortKey =
  | "created_at"
  | "updated_at"
  | "status"
  | "song_title"
  | "artist_name"
  | "priority";

export interface SongRequest {
  _id: string;
  request_id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  artist_name: string;
  song_title: string;
  song_link: string | null;
  album_name: string | null;
  genre: string | null;
  release_year: number | null;
  language: string | null;
  description: string | null;
  status: SongRequestStatus;
  priority: SongRequestPriority;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  estimated_completion: string | null;
  admin_notes: string | null;
  processed_by: string | null;
  completed_song_id: string | null;
}

export interface CreateSongRequestData {
  user_id: string;
  user_email: string;
  user_name: string;
  artist_name: string;
  song_title: string;
  song_link?: string | null;
  album_name?: string | null;
  genre?: string | null;
  release_year?: number | null;
  language?: string | null;
  description?: string | null;
  status?: SongRequestStatus;
  priority?: SongRequestPriority;
}

export interface UpdateSongRequestData {
  user_email?: string;
  user_name?: string;
  artist_name?: string;
  song_title?: string;
  song_link?: string | null;
  album_name?: string | null;
  genre?: string | null;
  release_year?: number | null;
  language?: string | null;
  description?: string | null;
  status?: SongRequestStatus;
  priority?: SongRequestPriority;
}

export interface SongRequestPushResult {
  push_status: SongRequestPushStatus;
  reason: string | null;
  targeted_devices: number;
  sent: number;
  failed: number;
  skipped: number;
}

export interface SongRequestStatusUpdateResult {
  request: SongRequest;
  push: SongRequestPushResult | null;
}

export interface SongRequestListParams {
  page?: number;
  limit?: number;
  status?: SongRequestStatus;
  user_id?: string;
  search?: string;
  sort_by?: SongRequestSortKey;
  sort_order?: "asc" | "desc";
}

export interface SongRequestListResponse {
  items: SongRequest[];
  meta: PaginationMeta | null;
}

const BASE = ApiEndpoints.ADMIN_SONG_REQUESTS_V1.replace(/\/$/, "");

function requestPath(id: string, suffix = "") {
  return `${BASE}/${encodeURIComponent(id)}${suffix}`;
}

function normalizeSongRequest(item: any): SongRequest {
  return {
    _id: String(item?._id || item?.id || ""),
    request_id: String(item?.request_id || item?.id || ""),
    user_id: String(item?.user_id || ""),
    user_email: String(item?.user_email || ""),
    user_name: String(item?.user_name || ""),
    artist_name: String(item?.artist_name || item?.artist || ""),
    song_title: String(item?.song_title || ""),
    song_link: nullableString(item?.song_link),
    album_name: nullableString(item?.album_name),
    genre: nullableString(item?.genre),
    release_year:
      typeof item?.release_year === "number" && Number.isFinite(item.release_year)
        ? item.release_year
        : null,
    language: nullableString(item?.language),
    description: nullableString(item?.description ?? item?.notes),
    status: Object.values(SongRequestStatus).includes(item?.status)
      ? item.status
      : SongRequestStatus.PENDING,
    priority: ["low", "normal", "high", "urgent"].includes(item?.priority)
      ? item.priority
      : "normal",
    is_deleted: Boolean(item?.is_deleted),
    created_at: String(item?.created_at || ""),
    updated_at: String(item?.updated_at || ""),
    deleted_at: nullableString(item?.deleted_at),
    estimated_completion: nullableString(item?.estimated_completion),
    admin_notes: nullableString(item?.admin_notes),
    processed_by: nullableString(item?.processed_by ?? item?.assigned_to),
    completed_song_id: nullableString(
      item?.completed_song_id ?? item?.published_song_id
    ),
  };
}

function nullableString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function normalizePushResult(item: any): SongRequestPushResult | null {
  if (!item || typeof item !== "object" || typeof item.push_status !== "string") {
    return null;
  }
  return {
    push_status: ["pending", "sent", "failed", "skipped"].includes(item.push_status)
      ? item.push_status
      : "pending",
    reason: nullableString(item.reason),
    targeted_devices: Number(item.targeted_devices || 0),
    sent: Number(item.sent || 0),
    failed: Number(item.failed || 0),
    skipped: Number(item.skipped || 0),
  };
}

function asCollection(payload: unknown): SongRequest[] {
  if (Array.isArray(payload)) {
    return payload.map(normalizeSongRequest).filter((item) => !item.is_deleted);
  }
  if (!payload || typeof payload !== "object") return [];
  const record = payload as Record<string, unknown>;
  const rows = Array.isArray(record.items)
    ? record.items
    : Array.isArray(record.requests)
      ? record.requests
      : [];
  return rows.map(normalizeSongRequest).filter((item) => !item.is_deleted);
}

export async function fetchAllSongRequestsPaged(
  params: SongRequestListParams = {}
): Promise<SongRequestListResponse> {
  const response = await httpService.get<
    SongRequest[] | { items?: SongRequest[]; requests?: SongRequest[] }
  >(BASE, { params });
  return {
    items: asCollection(response.data),
    meta: response.meta || null,
  };
}

export async function fetchAllSongRequests() {
  const response = await fetchAllSongRequestsPaged({ page: 1, limit: 100 });
  return response.items;
}

export async function fetchMySongRequests() {
  return fetchAllSongRequests();
}

export async function fetchSongRequest(id: string): Promise<SongRequest> {
  const response = await httpService.get<SongRequest>(requestPath(id));
  return normalizeSongRequest(response.data);
}

export async function createSongRequest(
  data: CreateSongRequestData
): Promise<SongRequest> {
  const response = await httpService.post<SongRequest>(BASE, data);
  return normalizeSongRequest(response.data);
}

export async function updateSongRequest(
  id: string,
  data: UpdateSongRequestData
): Promise<SongRequest> {
  const response = await httpService.put<SongRequest>(requestPath(id), data);
  return normalizeSongRequest(response.data);
}

export async function updateSongRequestStatus(
  requestId: string,
  status: SongRequestStatus,
  completedSongId?: string | null,
  adminNotes?: string | null
): Promise<SongRequestStatusUpdateResult> {
  const response = await httpService.put<any>(
    requestPath(requestId, "/status"),
    {
      status,
      completed_song_id:
        status === SongRequestStatus.COMPLETED ? completedSongId || null : null,
      admin_notes: adminNotes || null,
    }
  );
  const payload = response.data as any;
  const requestPayload =
    payload?.song_request || payload?.request || payload?.item || payload;
  const pushPayload =
    payload?.admin_notification ||
    payload?.notification ||
    payload?.push ||
    payload?.push_result ||
    payload;
  return {
    request: normalizeSongRequest(requestPayload),
    push: normalizePushResult(pushPayload),
  };
}

export async function approveSongRequest(
  requestId: string,
  completedSongId?: string
) {
  return updateSongRequestStatus(
    requestId,
    SongRequestStatus.COMPLETED,
    completedSongId
  );
}

export async function rejectSongRequest(requestId: string) {
  return updateSongRequestStatus(requestId, SongRequestStatus.REJECTED);
}

export async function deleteSongRequest(id: string): Promise<void> {
  await httpService.delete(requestPath(id));
}
